import type { ChangeEvent, JSX, ReactNode } from 'react';
import { useId } from 'react';
import styles from './controls.module.css';

/** Набор обычных элементов управления. Никаких самодельных виджетов. */

export function cx(...parts: (string | false | undefined | null)[]): string {
  return parts.filter(Boolean).join(' ');
}

export type SegmentOption<T extends string> = {
  value: T;
  label: string;
  title?: string;
  disabled?: boolean;
};

export type SegmentProps<T extends string> = {
  name: string;
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  ariaLabel?: string;
};

/** Группа радиокнопок: работает клавиатурой, читается скринридером. */
export function Segment<T extends string>({
  name,
  value,
  options,
  onChange,
  ariaLabel,
}: SegmentProps<T>): JSX.Element {
  const group = useId();
  return (
    <div className={styles.segment} role="radiogroup" aria-label={ariaLabel}>
      {options.map((option) => {
        const id = `${group}-${option.value}`;
        return (
          <span className={styles.segmentItem} key={option.value}>
            <input
              className={styles.segmentInput}
              type="radio"
              id={id}
              name={`${name}-${group}`}
              value={option.value}
              checked={value === option.value}
              disabled={option.disabled}
              onChange={() => onChange(option.value)}
            />
            <label className={styles.segmentLabel} htmlFor={id} title={option.title}>
              {option.label}
            </label>
          </span>
        );
      })}
    </div>
  );
}

export type ButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'ghost';
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  ariaLabel?: string;
};

export function Button({
  children,
  onClick,
  variant = 'default',
  disabled,
  type = 'button',
  title,
  ariaLabel,
}: ButtonProps): JSX.Element {
  return (
    <button
      type={type}
      className={cx(
        styles.button,
        variant === 'primary' && styles.primary,
        variant === 'ghost' && styles.ghost,
      )}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}

export type CheckboxProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
};

export function Checkbox({ checked, onChange, label, hint, disabled }: CheckboxProps): JSX.Element {
  return (
    <label className={styles.checkbox}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
      />
      <span className={styles.checkboxText}>
        <strong>{label}</strong>
        {hint ? <span>{hint}</span> : null}
      </span>
    </label>
  );
}

export const controlStyles = styles;
